import { FC, useMemo, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";

import Modal from "../modal/Modal";
import Button from "../button/Button";
import ChargeModal from "../charge-modal/ChargeModal";
import ThreadItem from "../thread/ThreadItem";
import type { Transaction } from "../../app/services/transactions";
import type { MeBalanceSource } from "../../utils/balance";
import { formatYenSigned } from "../../utils/money";
import {
  amountLabelForTransaction,
  computeTransactionsBalanceSummary,
} from "../../utils/transactions";

const GroupHeader = styled.div.attrs({ className: "bodytext-13" })`
  color: var(--soremo-light-gray);
  padding: 8px 0 0 0;
`;

const Empty = styled.div`
  color: var(--soremo-light-gray);
  text-align: center;
  padding: 24px 0;
`;

/**
 * 進行中モーダル上部のサマリ枠。
 *
 * 「オファーに使える残高」を主役に大きく出し、その内訳として
 * 残高 / チャージ済み / 入金予定 / 支払予定 の 4 行を並べる。
 * **4 行の符号付き合計が「オファーに使える残高」と一致**するよう
 * 設計しており、ユーザーが内訳と合計の関係を直感的に把握できる。
 */
const SummaryBox = styled.div`
  padding: 16px 12px;
  border: 1px solid var(--soremo-border);
  border-radius: 4px;
  background: var(--soremo-background);
`;

const SummaryRow = styled.div.attrs({
  className: "u-row-between u-w100",
})`
  padding: 2px 0;
`;

const SummaryDivider = styled.hr`
  border: none;
  border-top: 1px solid var(--soremo-border);
  margin: 4px 0;
`;

const AvailableLabelGroup = styled.span``;

const AvailableLabel = styled.span.attrs({ className: "heading-16" })``;

const TaxSuffix = styled.span.attrs({ className: "bodytext-11" })`
  color: var(--soremo-light-gray);
`;

const AvailableValue = styled.span.attrs({ className: "heading-18" })``;

const SummaryLabel = styled.span.attrs({ className: "bodytext-13" })``;

const SummaryValue = styled.span.attrs({ className: "bodytext-13" })<{
  $muted?: boolean;
}>`
  color: ${({ $muted }) => ($muted ? "var(--soremo-light-gray)" : "inherit")};
`;

// 「増やす」ボタンを金額の並び（右端）に揃えて置く行
const ChargeButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

interface TransactionsModalProps {
  transactions: Transaction[];
  /** 委託/受託の判定（ThreadItem 内）に使うログインユーザー ID */
  currentUserId?: number | null;
  /** `/api/me` の残高情報。上部サマリの算出に使う。未取得時は 0 表示。 */
  balanceSource?: MeBalanceSource | null;
  /**
   * クレジットカードによるチャージ（「増やす」ボタン）を許可するか。
   * 自分の残高を見ている場合のみ true にする。MANAGER の代理閲覧
   * （AccountTransactionsModal 経由）では決済カードが操作者のものに
   * なってしまうため無効のまま。
   */
  allowCharge?: boolean;
  onClose: () => void;
}

const formatYen = (value: number): string =>
  `¥${Math.round(value).toLocaleString("ja-JP")}`;

const formatYenWithSign = (value: number): string =>
  formatYenSigned(value) ?? "¥0";

/**
 * 進行中取引（全プロジェクト横断）の一覧モーダル。
 * DM 左スレッドと同じ `ThreadItem` で各取引を表示し、プロジェクト毎にグループ化する。
 * 行クリックで該当プロジェクトの DM スレッドへ遷移する。
 */
const TransactionsModal: FC<TransactionsModalProps> = ({
  transactions,
  currentUserId,
  balanceSource,
  allowCharge = false,
  onClose,
}) => {
  const navigate = useNavigate();
  const [isChargeModalOpen, setIsChargeModalOpen] = useState(false);

  // プロジェクト毎にグループ化（出現順を維持）
  const groups = useMemo(() => {
    const map = new Map<
      string,
      { projectName: string; items: Transaction[] }
    >();
    transactions.forEach((tx) => {
      const key = tx.project_id ?? "unknown";
      if (!map.has(key)) {
        map.set(key, {
          projectName: tx.project_name ?? "（プロジェクト名なし）",
          items: [],
        });
      }
      map.get(key)!.items.push(tx);
    });
    return Array.from(map.entries());
  }, [transactions]);

  // 上部サマリは ``computeTransactionsBalanceSummary`` で算出。
  // ``availableInclusive`` は BE ``escrow_lock`` の利用可能額と一致するため、
  // ここの表示と新仕様オファー送信時の残高判定が矛盾しないことが保証される。
  const summary = useMemo(
    () => computeTransactionsBalanceSummary(balanceSource),
    [balanceSource],
  );

  const handleOpen = (tx: Transaction) => {
    if (tx.project_id) {
      navigate(`/${tx.project_id}?tab=dm&thread=${tx.id}`);
    }
    onClose();
  };

  return (
    <Modal title="進行中" onClose={onClose} size="default">
      <SummaryBox>
        {/* 主役: オファーに使える残高 */}
        <SummaryRow>
          <AvailableLabelGroup>
            <AvailableLabel>オファーに使える残高</AvailableLabel>
            <TaxSuffix>（税込）</TaxSuffix>
          </AvailableLabelGroup>
          <AvailableValue>
            {formatYen(summary.availableInclusive)}
          </AvailableValue>
        </SummaryRow>

        {/* クレジットカードでチャージ額を増やす（自分の残高のみ）。
            他画面の追加系ボタン（新規プロジェクト作成 / IP 追加）と同じ
            filled + leftIcon="add" の標準形に揃える。 */}
        {allowCharge && (
          <ChargeButtonRow>
            <Button
              size="small"
              theme="primary"
              variant="filled"
              width="fit"
              leftIcon="add"
              onClick={() => setIsChargeModalOpen(true)}
            >
              増やす
            </Button>
          </ChargeButtonRow>
        )}

        <SummaryDivider />

        {/* 内訳 4 行（符号付き合計が「オファーに使える残高」と一致）。
            すぐ振込申請できる「残高」を先頭に置き、チャージ済み / 入金予定 /
            支払予定 の順で並べる。 */}
        <SummaryRow>
          <SummaryLabel>残高</SummaryLabel>
          <SummaryValue>
            {formatYenWithSign(summary.settledInclusive)}
          </SummaryValue>
        </SummaryRow>
        <SummaryRow>
          <SummaryLabel>チャージ済み</SummaryLabel>
          <SummaryValue>
            {formatYenWithSign(summary.chargedInclusive)}
          </SummaryValue>
        </SummaryRow>
        <SummaryRow>
          <SummaryLabel>入金予定</SummaryLabel>
          <SummaryValue>
            {formatYenWithSign(summary.incomingInclusive)}
          </SummaryValue>
        </SummaryRow>
        <SummaryRow>
          <SummaryLabel>支払予定</SummaryLabel>
          <SummaryValue $muted>
            {formatYenWithSign(-summary.outgoingInclusive)}
          </SummaryValue>
        </SummaryRow>
      </SummaryBox>

      {transactions.length === 0 ? (
        <Empty className="bodytext-13">進行中の取引はありません</Empty>
      ) : (
        groups.map(([key, group]) => (
          <div key={key}>
            <GroupHeader>{group.projectName}</GroupHeader>
            {group.items.map((tx) => (
              <ThreadItem
                key={tx.id}
                thread={tx}
                currentUserId={currentUserId ?? undefined}
                unreadCount={tx.unread_count}
                amountLabel={amountLabelForTransaction(tx)}
                onClick={() => handleOpen(tx)}
                isLegacyEnvironment={tx.is_new_payment_model === false}
              />
            ))}
          </div>
        ))
      )}

      {/* チャージ（残高を増やす）モーダル。成功すると /api/me が再取得され、
          背後の残高サマリも自動的に更新される。 */}
      {isChargeModalOpen && (
        <ChargeModal onClose={() => setIsChargeModalOpen(false)} />
      )}
    </Modal>
  );
};

export default TransactionsModal;
